import React, { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import identity from 'lodash/identity';
import property from 'lodash/property';

import { useTranslation } from '@lyearn/i18n';
import { ChevronDown } from '@lyearn/icons';
import { Autocomplete, Input } from '@lyearn/ui';
import { RenderInputParams } from '@lyearn/ui';

import { AutocompleteSearchProps } from './types';

const DISTANCE_FROM_END = 10;
const DEBOUNCE_DURATION = 200;

function AutocompleteSearch<T extends { id?: string; name?: string | null }>({
  className,
  classes,
  clearOnBlur,
  data,
  defaultValue,
  disabled,
  placeholder,
  initialValue,
  fetchMore,
  getTextFromValue = (value: T | null) => value?.name ?? '',
  groupBy,
  getOptionLabel,
  onChange,
  onInputChange,
  renderers,
  closeIcon,
  popupIcon,
  PopperComponent,
  size,
  value,
  ...rest
}: AutocompleteSearchProps<T>) {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState<string>(() => initialValue || '');
  useEffect(() => {
    if (!value && initialValue != inputValue) {
      setInputValue('');
    }
    if (inputValue != value?.name && value?.name) {
      setInputValue(value?.name);
    }
  }, [value]);
  const debouncedOnInputChange = useMemo(
    () =>
      debounce((searchValue: string) => {
        onInputChange(searchValue);
      }, DEBOUNCE_DURATION),
    [onInputChange],
  );

  const onTextChange = useCallback(
    (event: any, value: any, reason: any) => {
      if (reason === 'reset' && !event) {
        return; // ignore resetting initialValue
      }
      setInputValue(value);
      if (reason === 'reset') {
        debouncedOnInputChange('');
      } else {
        debouncedOnInputChange(event?.currentTarget?.value);
      }
    },
    [debouncedOnInputChange],
  );

  const _onChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: T | null) => {
      onInputChange('');
      onChange(value?.id, value);
    },
    [onChange, onInputChange],
  );

  const renderInput = (params: RenderInputParams) => {
    const { endAdornment, startAdornment, ...restInputParams } = params;

    return (
      <Input
        {...restInputParams}
        endAdornment={renderers?.renderEndAdornment?.(endAdornment) || endAdornment}
        startAdornment={renderers?.renderStartAdornment?.(startAdornment) || startAdornment}
      />
    );
  };

  return (
    <Autocomplete
      fullWidth
      openOnFocus
      ListboxProps={
        fetchMore
          ? {
              onScroll: (event: React.SyntheticEvent) => {
                const listboxNode = event.currentTarget;
                if (
                  listboxNode.scrollTop + listboxNode.clientHeight >=
                  listboxNode.scrollHeight - DISTANCE_FROM_END
                ) {
                  fetchMore();
                }
              },
            }
          : undefined
      }
      PopperComponent={PopperComponent}
      className={className}
      classes={classes}
      clearIcon={closeIcon}
      clearOnBlur={clearOnBlur}
      defaultValue={defaultValue}
      disabled={disabled}
      filterOptions={identity}
      getOptionLabel={getOptionLabel ?? property('name')}
      groupBy={groupBy}
      id="autocomplete-search"
      inputValue={inputValue}
      isOptionEqualToValue={(option: T, value: T) => option.id === value.id}
      noOptionsText={t('Common:NoResultsFound')}
      options={data}
      placeholder={placeholder}
      popupIcon={
        popupIcon || <ChevronDown className="h-20 w-20" fillColorToken="icon-placeholder" />
      }
      renderGroup={renderers?.renderGroup}
      renderInput={renderInput}
      renderOption={renderers?.renderOption}
      value={value}
      onInputChange={onTextChange}
      size={size}
      // @ts-ignore
      onChange={_onChange}
      {...rest}
    />
  );
}

export default AutocompleteSearch;
